import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  createContext,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

// Import IIFE bundle as raw string to inject into iframe context
// Must use explicit path to get the self-contained bundle (not ESM which has external imports)

import openBuilderComponentsScript from '@appcues/open-builder-components/iife?raw';
import { useFloatingWindowListener } from '@appcues/sonar-builder';

import { useSafeAreaInsetsOptional } from './SafeAreaInsetsContext';
import Background from './Background';

export interface SafeAreaProps {
  children: ReactNode;
  onReady?: (contextWindow: Window) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const SafeAreaContext = createContext<{
  contextWindow: Window | null;
}>({
  contextWindow: null,
});

/**
 * SafeArea renders content inside an iframe, providing viewport isolation.
 * The iframe's contentWindow is passed to children via context.
 *
 * Key features:
 * - Uses about:blank for same-origin access
 * - Uses React portal to render children into iframe's document body
 * - Fills parent container (100% width/height)
 * - Supports max dimensions with centering
 */
// Default insets when not wrapped in SafeAreaInsetsProvider
const DEFAULT_STATE = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  maxWidth: undefined,
  maxHeight: undefined,
};

const SafeArea: React.FC<SafeAreaProps> = ({
  children,
  onReady,
  className,
  style,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeBody, setIframeBody] = useState<HTMLElement | null>(null);
  const [contextWindow, setContextWindow] = useState<Window | null>(window);
  const isFloating = useFloatingWindowListener();

  // Use dynamic insets and max dimensions if within provider, otherwise use defaults
  const dynamicState = useSafeAreaInsetsOptional();
  const { top, right, bottom, left, maxWidth, maxHeight } =
    dynamicState ?? DEFAULT_STATE;

  const initializeIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const iframeDoc = iframe.contentDocument;
    const iframeWin = iframe.contentWindow;

    if (!iframeDoc || !iframeWin) return;

    // Set up basic document structure
    // Set a font-family which is what's used for 'auto' font inheritance in the standalone builder
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100%;
              font-family: sans-serif;
            }
          </style>
        </head>
        <body></body>
      </html>
    `);
    iframeDoc.close();

    // Inject open-builder-components script to register web components in iframe context
    const script = iframeDoc.createElement('script');
    script.textContent = openBuilderComponentsScript;
    iframeDoc.body.append(script);

    setIframeBody(iframeDoc.body);
    setContextWindow(iframeWin);

    if (onReady) {
      onReady(iframeWin);
    }
  }, [onReady]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    // Handle iframe load event
    const handleLoad = () => {
      initializeIframe();
    };

    iframe.addEventListener('load', handleLoad);

    // Initialize immediately if iframe is already loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      initializeIframe();
    }

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [initializeIframe]);

  const defaultStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    border: 'none',
    // Disable pointer events when a floating window is being dragged
    // to prevent the iframe from capturing mouse events
    ...(isFloating && { pointerEvents: 'none' }),
    ...style,
  };

  const contextValue = useMemo(
    () => ({
      contextWindow,
    }),
    [contextWindow]
  );

  // Calculate container styles based on insets and max dimensions
  const containerStyle: React.CSSProperties = {
    width: `calc(100% - ${left + right}px)`,
    height: `calc(100% - ${top + bottom}px)`,
    border: '1px solid var(--border-neutral-1)',
    borderRadius: '6px',
    background: 'var(--bg-neutral-1)',
    // Apply max dimensions if set
    ...(maxWidth && { maxWidth }),
    ...(maxHeight && { maxHeight }),
    // Center when max dimensions are applied
    ...((maxWidth !== undefined || maxHeight !== undefined) && {
      margin: '0 auto',
    }),
  };

  return import.meta.env.STANDALONE ? (
    <div style={containerStyle}>
      <iframe
        ref={iframeRef}
        src="about:blank"
        className={className}
        style={defaultStyle}
        title="Safe Area Content"
      />
      {iframeBody &&
        contextWindow &&
        createPortal(
          <SafeAreaContext.Provider value={contextValue}>
            {children}
            <Background />
          </SafeAreaContext.Provider>,
          iframeBody
        )}
    </div>
  ) : (
    <SafeAreaContext.Provider value={contextValue}>
      {children}
    </SafeAreaContext.Provider>
  );
};

export default SafeArea;
